import React, { useEffect, useMemo, useState } from "react";
import { PageContainer, PageHeader, Card } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CaretDown, ArrowSquareOut, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Detail Pages Manager
 * -------------------------------------------------------------
 * Provides an admin editor for the JSON blobs that power:
 *   - Editorial pages       (`page.*`)   e.g. /who-we-are
 *   - Service detail pages  (`spage.*`)  e.g. /services/digital-marketing
 *   - Service detail (v8)   (`service.*.detail`) (legacy variant)
 *
 * Each page is stored in `page_content` as { key, value:JSON string }.
 * We use the existing GET/PUT /admin/page-content endpoints.
 */

// Human-friendly labels + public URL per key prefix
function keyToMeta(key) {
  // spage.<parent>.<slug>
  if (key.startsWith("spage.")) {
    const rest = key.slice("spage.".length);
    const parts = rest.split(".");
    if (parts.length === 1) {
      return {
        group: "Service pages (main)",
        title: `Service · ${parts[0]}`,
        url: `/services/${parts[0]}`,
      };
    }
    return {
      group: "Service pages (sub-services)",
      title: `Sub-service · ${parts[0]} → ${parts[1]}`,
      url: `/services/${parts[0]}/${parts[1]}`,
    };
  }
  // service.<slug>.detail (legacy variant)
  if (key.startsWith("service.") && key.endsWith(".detail")) {
    const slug = key.slice("service.".length, -".detail".length);
    return {
      group: "Service details (legacy)",
      title: `Service detail (legacy) · ${slug}`,
      url: `/services/${slug}`,
    };
  }
  // page.<slug>
  if (key.startsWith("page.")) {
    const slug = key.slice("page.".length);
    return {
      group: "Editorial pages",
      title: `Editorial · ${slug}`,
      url: `/${slug}`,
    };
  }
  return { group: "Other", title: key, url: null };
}

const SCHEMA_HINTS = {
  "spage.": `{
  "h1": "Digital marketing services in the UAE",
  "hero_copy": "Short lead paragraph…",
  "seo_title": "…",
  "meta_description": "…",
  "challenge": "Business challenge paragraph…",
  "help": "How OpSys Digital helps paragraph…",
  "core_areas": [
    { "title": "SEO", "desc": "…", "icon": "magnifying-glass" }
  ],
  "sub_services": [
    { "slug": "seo-services", "title": "SEO Services", "blurb": "…", "icon": "magnifying-glass" }
  ],
  "use_cases": ["…"] or [{ "title": "…", "desc": "…" }],
  "included": ["…"],
  "outcomes": ["…"],
  "how_we_deliver": [{ "step": "Discovery", "desc": "…" }],
  "faqs": [{ "q": "…", "a": "…" }],
  "final_cta": { "heading": "…", "body": "…", "text": "Request a Consultation" },
  "parent_slug": "digital-marketing"   // only for sub-service pages
}
// icon slugs (leave blank to fall back to a neutral Sparkle):
//   lightning · buildings · shield · briefcase · sparkle · cpu · gauge · plugs
//   solar · fan · thermometer · lightbulb · cube · factory · bank · storefront
//   broadcast · package · wrench · star · target · rocket · rocket-launch
//   chart-line · chart-bar · megaphone · code · robot · broom · headset
//   envelope · magnifying-glass · device-mobile · globe · database · lock`,
  "service.": `{
  "h1": "…",
  "hero_copy": "…",
  ...same shape as spage.* JSON
}`,
  "page.": `{
  "h1": "About OpSysDigital",
  "hero_copy": "…",
  "seo_title": "…",
  "meta_description": "…",
  "sections": [
    { "h2": "Section heading", "body": "…", "bullets": ["…"],
      "subsections": [{ "h3": "…", "body": "…", "bullets": ["…"] }],
      "pairs": [{ "need": "…", "help": "…" }] }
  ],
  "snippet": "One-line summary shown at bottom.",
  "cta_heading": "…", "cta_body": "…", "cta_text": "Contact us"
}`,
};

function schemaHintFor(key) {
  if (key.startsWith("spage.")) return SCHEMA_HINTS["spage."];
  if (key.startsWith("service.")) return SCHEMA_HINTS["service."];
  if (key.startsWith("page.")) return SCHEMA_HINTS["page."];
  return "";
}

function pretty(raw) {
  if (!raw) return raw;
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

function DetailPageRow({ pageKey, initialValue, onSaved }) {
  const meta = useMemo(() => keyToMeta(pageKey), [pageKey]);
  const [value, setValue] = useState(pretty(initialValue) || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setValue(pretty(initialValue) || ""); setDirty(false); }, [initialValue]);

  const validate = () => {
    if (!value.trim()) return "Value cannot be empty (delete the row would remove the page — this UI does not support that).";
    try { JSON.parse(value); } catch (e) { return `Invalid JSON: ${e.message}`; }
    return "";
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); toast.error(err); return; }
    setError("");
    setSaving(true);
    try {
      // Store as compact JSON (matches how PageContentManager saves JSON blobs).
      const compact = JSON.stringify(JSON.parse(value));
      await adminApi.put("/admin/page-content", { key: pageKey, value: compact });
      toast.success("Saved");
      setDirty(false);
      onSaved && onSaved(pageKey, compact);
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const format = () => setValue(pretty(value));

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`detail-page-${pageKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition text-left"
      >
        <div className="min-w-0">
          <div className="font-heading font-semibold text-[14.5px] tracking-tight truncate" style={{ color: "#0F2A6E" }}>
            {meta.title}
          </div>
          <div className="text-[11.5px] text-[#6B7A8D] font-mono truncate">{pageKey}</div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          {dirty && <span className="text-[10.5px] font-semibold" style={{ color: "#1B6FE0" }}>UNSAVED</span>}
          {meta.url && (
            <a
              href={meta.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[12px] font-medium hover:underline"
              style={{ color: "#1B6FE0" }}
              data-testid={`detail-page-open-${pageKey}`}
            >
              <ArrowSquareOut size={12} weight="bold" /> Open live page
            </a>
          )}
          <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[12px] text-[#6B7A8D]">Content JSON</Label>
            <button
              type="button"
              onClick={format}
              className="text-[11.5px] font-medium hover:underline"
              style={{ color: "#1B6FE0" }}
              data-testid={`detail-format-${pageKey}`}
            >
              Format JSON
            </button>
          </div>
          <Textarea
            rows={22}
            value={value}
            onChange={(e) => { setValue(e.target.value); setDirty(true); setError(""); }}
            className="font-mono text-[12.5px] leading-relaxed"
            spellCheck={false}
            data-testid={`detail-json-${pageKey}`}
          />
          {error && (
            <div className="mt-2 text-[12.5px]" style={{ color: "#1B6FE0" }}>{error}</div>
          )}
          <details className="mt-3">
            <summary className="text-[11.5px] text-slate-500 cursor-pointer">Show expected JSON schema</summary>
            <pre className="mt-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-[11.5px] leading-snug overflow-x-auto whitespace-pre">
{schemaHintFor(pageKey)}
            </pre>
          </details>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setValue(pretty(initialValue) || ""); setDirty(false); setError(""); }}
              data-testid={`detail-revert-${pageKey}`}
            >
              Revert
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="text-white disabled:opacity-50"
              style={{ background: "#1B6FE0" }}
              data-testid={`detail-save-${pageKey}`}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DetailPagesManager() {
  const [all, setAll] = useState({}); // {key: value}
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/page-content");
      setAll(data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Only keys that represent a detail page (JSON blobs)
  const keys = useMemo(() => Object.keys(all).filter(
    (k) => k.startsWith("page.") || k.startsWith("spage.") || (k.startsWith("service.") && k.endsWith(".detail"))
  ), [all]);

  // Filter + group
  const groups = useMemo(() => {
    const filtered = q.trim()
      ? keys.filter((k) => k.toLowerCase().includes(q.trim().toLowerCase()))
      : keys;
    const bucket = {};
    for (const k of filtered) {
      const g = keyToMeta(k).group;
      (bucket[g] = bucket[g] || []).push(k);
    }
    // Preserve a friendly group order
    const order = [
      "Editorial pages",
      "Service pages (main)",
      "Service pages (sub-services)",
      "Service details (legacy)",
      "Other",
    ];
    const sorted = {};
    for (const name of order) if (bucket[name]) sorted[name] = bucket[name].sort();
    return sorted;
  }, [keys, q]);

  const onSaved = (key, value) => setAll((prev) => ({ ...prev, [key]: value }));

  return (
    <PageContainer>
      <PageHeader
        title="Detail Pages"
        subtitle="Edit the content of Editorial (/who-we-are, /what-we-do, /why-choose-us, /our-core-team) and Service detail pages (/services/…) as JSON. Changes go live immediately."
      />

      <Card className="p-4 mb-4">
        <div className="flex items-center gap-2 text-[13px]">
          <MagnifyingGlass size={14} weight="bold" className="text-slate-400" />
          <Input
            placeholder="Filter pages by key (e.g. digital-marketing, who-we-are, seo-services)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 text-[13px]"
            data-testid="detail-filter"
          />
        </div>
      </Card>

      {loading && <Card className="p-8 text-center text-[#6B7A8D]">Loading…</Card>}

      {!loading && Object.keys(groups).length === 0 && (
        <Card className="p-8 text-center text-[#6B7A8D]">No matching detail pages.</Card>
      )}

      {!loading && Object.entries(groups).map(([group, gKeys]) => (
        <div key={group} className="mb-6" data-testid={`detail-group-${group.replace(/[^a-z0-9]+/gi,"-").toLowerCase()}`}>
          <h3 className="text-[11.5px] uppercase tracking-[0.18em] font-semibold mb-2 px-1" style={{ color: "#6B7A8D" }}>
            {group} <span className="text-slate-400 normal-case tracking-normal font-normal">· {gKeys.length}</span>
          </h3>
          <div className="space-y-2">
            {gKeys.map((k) => (
              <DetailPageRow key={k} pageKey={k} initialValue={all[k]} onSaved={onSaved} />
            ))}
          </div>
        </div>
      ))}
    </PageContainer>
  );
}
